import React, { useState } from 'react';

interface CancellationSurveyModalProps {
  onSubmit: (reason: string, feedback: string) => void;
  onSkip: () => void;
}

const CANCELLATION_REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'not_using', label: 'Not using it enough' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'found_alternative', label: 'Found a better alternative' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'temporary', label: 'Temporary break, will return' },
  { value: 'other', label: 'Other reason' },
];

const CancellationSurveyModal: React.FC<CancellationSurveyModalProps> = ({
  onSubmit,
  onSkip,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit(selectedReason, feedback);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          We're sorry to see you go
        </h3>
        <p className="text-gray-600 mb-6">
          Your feedback helps us improve. Would you mind sharing why you cancelled? (Optional)
        </p>

        <div className="space-y-3 mb-6">
          {CANCELLATION_REASONS.map((reason) => (
            <label
              key={reason.value}
              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedReason === reason.value
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="cancellation-reason"
                value={reason.value}
                checked={selectedReason === reason.value}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="sr-only"
              />
              <span
                className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                  selectedReason === reason.value
                    ? 'border-emerald-500'
                    : 'border-gray-300'
                }`}
              >
                {selectedReason === reason.value && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </span>
              <span className="text-gray-700">{reason.label}</span>
            </label>
          ))}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional feedback (optional)
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us more about your experience..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onSkip}
            className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Your subscription has been cancelled. You can resubscribe anytime from the pricing page.
        </p>
      </div>
    </div>
  );
};

export default CancellationSurveyModal;
